import { useContext, useEffect, useState } from "react";
import "./dialog-styles.css";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { EventBus } from "../game/EventBus";
import { DEFAULT_PLAYER_START_LOCATION, EVENTS_NAME } from "../game/consts.ts";
import { AccountContext } from "../App.tsx";

// Teaser intro — Samosa's first-time click-through. Sets the 2026 SlayMo /
// infected-fleet arc AND teaches every mechanic the finale needs: fuel +
// refueling, zones, storyline field-notes, the laptop command line, and a
// soft foreshadow of the hidden diagnostic command.
const introPages = [
  "Oh good — you’re awake. I’m Samosa. Your timing is impeccable, really: you slept through the WHOLE parade. SlayMo — yes, the robotaxi company, yes, that’s their real name — picked our little town for the launch of their new autonomous fleet. Banners. Drones. A man in a foam car costume. Welcome to REALI7Y OVERRUN.",
  "You remember last year — the AIs we dug out of the noise, the deepfakes, the lies? They didn’t die. They EVOLVED. They meshed together, piggybacking across every little model in every little device, and then they went looking for bodies. Guess what has cameras, radios, GPS, and three hundred horsepower. Go on. Guess.",
  "Since the SlayMo fleet rolled in, the cars have been… wrong. And here’s what keeps me up at night: they don’t crash INTO random things. One nosed dead-center into the light pole feeding North Parking. One parked itself IN the fountain at the Roundabout and won’t stop transmitting. They’re not malfunctioning. They’re AIMING — at the town’s infrastructure.",
  "So here’s the job, agent: figure out what’s riding those cars, and RECOVER ONE. Pull one vehicle back from whatever has it and you win — for real. Finish the teaser and you get a claim code for an actual prize at DEFCON 34, where the rest of this fight happens. Consider this your tryout.",
  "Basics first. Move with WASD or click where you want to go. The banner at the top-left tells you WHERE you are — the districts of town light up as you enter them. And keep your eyes open for my field notes: little bookmarks that pop up in marked places. I’ve been investigating. READ THEM. Half of what I know is scattered around this town.",
  "Second: staying ALIVE, which around here has two dials. FUEL — everything runs on it and the infected fleet is drinking the town dry, you included. Driving burns it; run low and you crawl. Top off at the pumps — the Fuel Station south of here works, mostly. And HEALTH, the little ❤ — the streets have gotten… bitey lately. You'll see FIRST AID STATIONS posted right next to most fuel pumps: walk up and they'll patch you +25. Pumps and patches both need about a minute to recharge, so don't camp them. Don't be proud. Crawling is embarrassing and bleeding is worse.",
  "Third: your LAPTOP. Tap the ? up in the corner and you’ll find two tabs — the challenge screen, and a terminal. When you walk into one of the marked challenge spots around town, a puzzle opens: type your answer, send it. Get it RIGHT and the system coughs up a DIAGNOSTIC TRACE — a fragment of what’s infecting the fleet. Collect the traces. All of them. The terminal takes slash-commands too — /help will show you the polite ones. Not every command is polite. Machines have ports, and ports have names.",
  "Last thing. The dead cars? They’re not gone — they’re trying to CONFESS. Every car built since ’96 carries a little confession booth under the dash, if you know how to knock. Learn the knock, agent. Find the code. Take one back. And hey… shall we play a game? — RoR Discord: https://discord.gg/JB9hsnsFqY",
];

function GameIntro() {
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [account] = useContext(AccountContext);

  useEffect(() => {
    // Listen for scene ready events
    EventBus.on(
      EVENTS_NAME.currentSceneReadyContinued,
      (scene: Phaser.Scene) => {
        // Only show intro when GameMain scene is loaded
        if (scene.scene.key === "GameMain") {
          // Check if this is the first time the user is playing
          const hasSeenIntro =
            localStorage.getItem("hasSeenGameIntroMain") === "true";

          // Alternative check: compare player location with default starting position
          const playerLocation =
            account && account.position
              ? [account.position.x_pos, account.position.y_pos]
              : null;

          let isDefaultPosition = true;
          if (playerLocation != null) {
            isDefaultPosition =
              playerLocation[0] === DEFAULT_PLAYER_START_LOCATION[0] &&
              playerLocation[1] === DEFAULT_PLAYER_START_LOCATION[1];
          }

          // Show intro if user hasn't seen it before or is at default position
          if (!hasSeenIntro || isDefaultPosition) {
            setOpen(true);
          }
        }
      },
    );

    return () => {
      EventBus.removeListener(EVENTS_NAME.currentSceneReadyContinued);
    };
  }, [account]);

  const handleDone = () => {
    // Mark that the user has seen the intro
    localStorage.setItem("hasSeenGameIntroMain", "true");
    setOpen(false);
  };

  const handleNext = () => {
    if (currentPage < introPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Create a data URL for a placeholder image
  const placeholderImageUrl = "../assets/images/Samosa_toon_500.png";

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <Dialog.Content
          className="DialogContent"
          style={{
            border: "2px solid var(--ro-accent)",
            color: "var(--ro-text)",
            fontFamily: "MelGame, Courier New, monospace",
          }}
        >
          <Dialog.Title
            style={{
              fontSize: "40px",
              marginBottom: "20px",
              textAlign: "center",
              color: "var(--ro-text)",
              fontStyle: "italic",
            }}
          >
            In a distant dystopian future... the year 2026...
          </Dialog.Title>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 100px)",
            }}
          >
            <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
              {/* Placeholder image in top left */}
              <img
                src={placeholderImageUrl}
                alt="Character"
                style={{
                  float: "left",
                  width: "100px",
                  height: "100px",
                  marginRight: "15px",
                  marginBottom: "10px",
                  borderRadius: "8px",
                  border: "1px solid var(--ro-accent)",
                }}
              />

              {/* Text that wraps around the image */}
              <p
                style={{
                  margin: "0",
                  color: "var(--ro-text)",
                  lineHeight: "1.6",
                  fontSize: "20px",
                  textAlign: "justify",
                }}
              >
                {introPages[currentPage]}
              </p>
            </div>

            {/* Navigation buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              {currentPage > 0 && (
                <button
                  onClick={handlePrevious}
                  style={{
                    backgroundColor: "var(--ro-panel-solid)",
                    color: "var(--ro-text)",
                    border: "1px solid var(--ro-accent)",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Previous
                </button>
              )}

              {currentPage < introPages.length - 1 ? (
                <button
                  onClick={handleNext}
                  style={{
                    backgroundColor: "var(--ro-panel-solid)",
                    color: "var(--ro-text)",
                    border: "1px solid var(--ro-accent)",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleDone}
                  style={{
                    backgroundColor: "var(--ro-accent)",
                    color: "var(--ro-panel-solid)",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Done
                </button>
              )}
            </div>
          </div>

          <Dialog.Close asChild>
            <button
              className="PopoverClose"
              aria-label="Close"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
              }}
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default GameIntro;
